const CHECK_INTERVAL_MS = 5 * 60 * 1000
const VERSION_QUERY_PARAM = '__app_version'

export function getEmbeddedVersion(documentObject = document) {
  return documentObject.querySelector('meta[name="app-version"]')?.content?.trim() || ''
}

export function buildReloadUrl(currentUrl, version) {
  const url = new URL(currentUrl)
  url.searchParams.set(VERSION_QUERY_PARAM, version)
  return url.toString()
}

async function clearCacheStorage(cacheStorage) {
  if (!cacheStorage?.keys || !cacheStorage?.delete) return

  try {
    const cacheNames = await cacheStorage.keys()
    await Promise.allSettled(cacheNames.map(cacheName => cacheStorage.delete(cacheName)))
  } catch {
    // Cache cleanup is best-effort and must never prevent the reload.
  }
}

async function unregisterServiceWorkers(serviceWorkerContainer) {
  if (!serviceWorkerContainer?.getRegistrations) return

  try {
    const registrations = await serviceWorkerContainer.getRegistrations()
    await Promise.allSettled(registrations.map(registration => registration.unregister()))
  } catch {
    // Old service workers are optional cleanup; the cache-busted reload still runs.
  }
}

export function createAppUpdateChecker({
  currentVersion,
  fetchImpl,
  locationObject,
  cacheStorage,
  serviceWorkerContainer,
  now = Date.now,
}) {
  let checkInProgress = false
  let updateInProgress = false

  async function installUpdate(version) {
    if (updateInProgress) return
    updateInProgress = true

    await Promise.allSettled([
      clearCacheStorage(cacheStorage),
      unregisterServiceWorkers(serviceWorkerContainer),
    ])

    locationObject.replace(buildReloadUrl(locationObject.href, version))
  }

  async function check() {
    if (checkInProgress || updateInProgress) return
    checkInProgress = true

    try {
      const endpoint = new URL('/version.json', locationObject.href)
      endpoint.searchParams.set('_', String(now()))
      const response = await fetchImpl(endpoint.toString(), { cache: 'no-store' })
      if (!response.ok) return

      const body = await response.json()
      const remoteVersion = typeof body?.version === 'string' ? body.version.trim() : ''
      if (remoteVersion && remoteVersion !== currentVersion) await installUpdate(remoteVersion)
    } catch {
      // Network and invalid-response failures are retried by the next scheduled check.
    } finally {
      checkInProgress = false
    }
  }

  return { check }
}

export function startAppUpdateChecker({
  windowObject = window,
  documentObject = document,
  intervalMs = CHECK_INTERVAL_MS,
} = {}) {
  const currentVersion = getEmbeddedVersion(documentObject)
  if (!currentVersion) return () => {}

  const checker = createAppUpdateChecker({
    currentVersion,
    fetchImpl: windowObject.fetch.bind(windowObject),
    locationObject: windowObject.location,
    cacheStorage: windowObject.caches,
    serviceWorkerContainer: windowObject.navigator?.serviceWorker,
  })

  const check = () => { void checker.check() }
  const checkWhenVisible = () => {
    if (documentObject.visibilityState === 'visible') check()
  }

  check()
  const intervalId = windowObject.setInterval(check, intervalMs)
  documentObject.addEventListener('visibilitychange', checkWhenVisible)
  windowObject.addEventListener('focus', check)
  windowObject.addEventListener('online', check)

  return () => {
    windowObject.clearInterval(intervalId)
    documentObject.removeEventListener('visibilitychange', checkWhenVisible)
    windowObject.removeEventListener('focus', check)
    windowObject.removeEventListener('online', check)
  }
}
